"use client"

import { useState, useEffect, useCallback, useMemo } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEdit, faArrowsUpDownLeftRight, faTrash } from "@fortawesome/free-solid-svg-icons"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import Tabs from "./modules/Tabs/Tabs"
import Button from "./modules/Button/Button"
import SearchBar from "../shared-modules/SearchBar"
import Table from "../table"
import Badge from "./modules/Badge/Badge"
import Select from "./modules/Select/Select"
import styles from "../../pages/inventory/InventoryDashboardPage.module.css"
import { getSupplies, updateSupply, deleteSupply } from "../../services/inventory/detail"
import EditSupplyForm from "./modal/EditSupplyForm"
import Modal from "./modules/Modal/Moadal" 
import MoveSupplyForm from "./modal/MoveSupplyForm"
import toast from "react-hot-toast"
import { parseDateSafe } from "../../utils/dateFormatters"

const SuppliesSection = ({ locationOptions = [], isAdmin, onMoveSupplyModal, onDeleteSupply, refreshTrigger }) => {
  const queryClient = useQueryClient()

  const [suppliesTab, setSuppliesTab] = useState("tenovi")
  const [selectedSupplies, setSelectedSupplies] = useState([])
  const [selectedIndividualSupplies, setSelectedIndividualSupplies] = useState([])
  const [suppliesSearchTerm, setSuppliesSearchTerm] = useState("")
  const [bulkSelectedLocation, setBulkSelectedLocation] = useState("all")

  const [editModal, setEditModal] = useState({ isOpen: false, selectedSupply: null })
  const [moveModal, setMoveModal] = useState({ isOpen: false, selectedSupplies: [], isBulkMove: false })

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    supply: null,
    loading: false,
    title: "Confirm Delete",
    message: "",
  })

  const statusColorMap = {
    "In Warehouse": "info",
    "Yet to be Received": "warning",
    "In Warehouse (Refurbished)": "success",
    Distributed: "success",
    "Pending Creation": "error",
    Pending: "warning",
    Available: "success",
    Lost: "error",
    Assigned: "info",
  }

  const resolveLocationId = (options, selected) => {
    if (!selected || selected === "all") return null
    if (!options || !Array.isArray(options)) {
      return selected
    }

    let found = options.find((o) => {
      const val = o.value !== undefined ? o.value : o.id
      const label = o.label !== undefined ? o.label : o.name || o.city
      return String(val) === String(selected) || String(label) === String(selected)
    })
    if (found) return found.value !== undefined ? found.value : found.id

    if (!isNaN(selected)) return selected

    return null
  }

  const suppliesQueryKey = ["supplies", suppliesTab, bulkSelectedLocation]

  const {
    data: suppliesData = [],
    isLoading: suppliesLoading,
    isFetching: suppliesFetching,
  } = useQuery(
    suppliesQueryKey,
    async () => {
      const filters = {}
      const locId = resolveLocationId(locationOptions, bulkSelectedLocation)
      if (locId) filters.location_id = locId

      const res = Object.keys(filters).length ? await getSupplies(suppliesTab, filters) : await getSupplies(suppliesTab)

      let list = []
      if (!res) return []
      if (Array.isArray(res.data)) list = res.data
      else if (Array.isArray(res.results)) list = res.results
      else if (Array.isArray(res)) list = res
      else if (res.data?.results) list = res.data.results
      else if (res.data) list = Array.isArray(res.data) ? res.data : []

      return list.map((s) => ({
        id: s.id,
        caseManager: s.added_by_name || "N/A",
        typeOfSupply: s.supply_type?.type || "N/A",
        itemName: s.supply_type?.name || s.name || "N/A",
        date: (() => { const _d = parseDateSafe(s.created_at); return _d ? `${_d.getFullYear()}-${String(_d.getMonth()+1).padStart(2,'0')}-${String(_d.getDate()).padStart(2,'0')}` : ""; })(),
        location: s.location_name || s.location?.name || "N/A",
        quantity: s.quantity,
        status: s.status,
        raw: s,
      }))
    },
    {
      staleTime: 1000 * 60, 
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    },
  )

  useEffect(() => {
    if (refreshTrigger) {
      queryClient.invalidateQueries(["supplies"], { exact: false })
    }
  }, [refreshTrigger, queryClient])

  const updateSupplyMutation = useMutation(({ id, payload }) => updateSupply(id, payload), {
    onMutate: async ({ id, payload }) => {
      await queryClient.cancelQueries(suppliesQueryKey)
      const previous = queryClient.getQueryData(suppliesQueryKey) || []

      queryClient.setQueryData(suppliesQueryKey, (old = []) => {
        return (old || []).map((row) => (String(row.id) === String(id) ? { ...row, ...payload } : row))
      })

      return { previous }
    },
    onError: (err, variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(suppliesQueryKey, context.previous)
      }
      toast.error("Failed to update supply")
      console.error("updateSupply error:", err)
    },
    onSettled: () => {
      queryClient.invalidateQueries(suppliesQueryKey, { exact: false })
    },
  })

  const deleteSupplyMutation = useMutation((id) => deleteSupply(id), {
    onMutate: async (id) => {
      await queryClient.cancelQueries(suppliesQueryKey)
      const previous = queryClient.getQueryData(suppliesQueryKey) || []
      queryClient.setQueryData(suppliesQueryKey, (old = []) => (old || []).filter((r) => String(r.id) !== String(id)))
      return { previous }
    },
    onError: (err, id, context) => {
      if (context?.previous) {
        queryClient.setQueryData(suppliesQueryKey, context.previous)
      }
      toast.error("Failed to delete supply")
      console.error("deleteSupply error:", err)
    },
    onSuccess: (res) => {
      toast.success("Supply deleted")
    },
    onSettled: () => {
      queryClient.invalidateQueries(suppliesQueryKey, { exact: false })
    },
  })

  const handleEditSupply = (supply) => {
    setEditModal({ isOpen: true, selectedSupply: supply })
  }

  const handleDeleteSupply = (supply) => {
    setConfirmModal({
      isOpen: true,
      supply,
      loading: false,
      title: "Delete Supply",
      message: `Are you sure you want to delete supply: ${supply.itemName}? This action cannot be undone.`,
    })
  }

  const confirmDelete = async () => {
    if (!confirmModal.supply) return
    setConfirmModal((p) => ({ ...p, loading: true }))
    try {
      await deleteSupplyMutation.mutateAsync(confirmModal.supply.id)

      if (typeof onDeleteSupply === "function") {
        try {
          onDeleteSupply(confirmModal.supply)
        } catch (err) {
          console.warn("onDeleteSupply callback error:", err)
        }
      }
    } catch (err) {

    } finally {
      setConfirmModal({ isOpen: false, supply: null, loading: false, title: "", message: "" })
    }
  }

  const handleMoveSingleSupply = (supply) => {
    setMoveModal({
      isOpen: true,
      selectedSupplies: [supply],
      isBulkMove: false,
    })
  }

  const handleMoveSelectedSupplies = async () => {
    const selectedIds = suppliesTab === "tenovi" ? selectedSupplies : selectedIndividualSupplies
    const supplyList = suppliesTab === "tenovi" ? suppliesData : suppliesData 
    if (selectedIds.length === 0) {
      alert("Please select supplies to move")
      return
    }

    const selectedSupplyItems = supplyList.filter((supply) => selectedIds.includes(supply.id))
    setMoveModal({
      isOpen: true,
      selectedSupplies: selectedSupplyItems,
      isBulkMove: true,
    })
  }

  const handleMoveAllSupplies = async () => {
    const supplyList = suppliesData || []
    setMoveModal({
      isOpen: true,
      selectedSupplies: supplyList,
      isBulkMove: true,
    })
  }

  const handleUpdateSupply = async (formData) => {
    if (!editModal.selectedSupply) return
    const payload = {
      status: formData.status,
      quantity: formData.quantity,
    }
    try {
      await updateSupplyMutation.mutateAsync({ id: editModal.selectedSupply.id, payload })
      setEditModal({ isOpen: false, selectedSupply: null })
    } catch (err) {

    }
  }

  const ActionButtons = ({ item }) => (
    <div className={styles.action_buttons}>
      <button
        className={styles.action_btn}
        onClick={(e) => {
          e.stopPropagation()
          handleEditSupply(item)
        }}
        title="Edit"
      >
        <FontAwesomeIcon icon={faEdit} />
      </button>
      <button
        className={styles.action_btn}
        onClick={(e) => {
          e.stopPropagation()
          handleMoveSingleSupply(item)
        }}
        title="Move"
      >
        <FontAwesomeIcon icon={faArrowsUpDownLeftRight} />
      </button>
      <button
        className={styles.action_btn}
        onClick={(e) => {
          e.stopPropagation()
          handleDeleteSupply(item)
        }}
        title="Delete"
      >
        <FontAwesomeIcon icon={faTrash} />
      </button>
    </div>
  )

  const baseColumns = [
    { key: "caseManager", label: "Case Manager", alignLeft: true },
    { key: "typeOfSupply", label: "Type of Supply", alignLeft: true },
    { key: "itemName", label: "Item Name", alignLeft: true, sorting: false },
    { key: "date", label: "Date", alignLeft: true },
    { key: "location", label: "Location", alignLeft: true },
    { key: "quantity", label: "Quantity", alignLeft: true, sorting: false },
    {
      key: "status",
      label: "Status",
      sorting: false,
      alignLeft: true,
      render: (supply) => (
        <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
          <Badge variant={statusColorMap[supply.status] || "default"} size="medium" dot={true}>
            {supply.status}
          </Badge>
        </div>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      sorting: false,
      render: (supply) => <ActionButtons item={supply} />,
    },
  ]

  const filterSupplies = useCallback(
    (list, searchTerm) => {
      if (!searchTerm) return list
      const term = searchTerm.toLowerCase()
      return (list || []).filter(
        (s) =>
          String(s.caseManager || "").toLowerCase().includes(term) ||
          String(s.typeOfSupply || "").toLowerCase().includes(term) ||
          String(s.itemName || "").toLowerCase().includes(term) ||
          String(s.location || "").toLowerCase().includes(term) ||
          String(s.status || "").toLowerCase().includes(term),
      )
    },
    [],
  )

  const filteredSupplyList = useMemo(() => filterSupplies(suppliesData || [], suppliesSearchTerm), [suppliesData, suppliesSearchTerm, filterSupplies])

  const suppliesTabs = [
    { id: "tenovi", label: "Tenovi" },
    { id: "internal", label: "Internal" },
  ]

  const getCurrentSelectedSupplies = () => (suppliesTab === "tenovi" ? selectedSupplies : selectedIndividualSupplies)
  const getCurrentSupplySelectionHandler = () => (suppliesTab === "tenovi" ? setSelectedSupplies : setSelectedIndividualSupplies)
  const getCurrentSupplyList = () => filteredSupplyList

  return (
    <div className={styles.supplies_content}>
      <div className={styles.tabs_container}>
        <Tabs tabs={suppliesTabs} activeTab={suppliesTab} onChange={setSuppliesTab} variant="secondary" size="small" />
      </div>

      <div className={styles.search_section}>
        <SearchBar
          searchTerm={suppliesSearchTerm}
          onSearchChange={setSuppliesSearchTerm}
          placeholder="Search Supplies"
          maxWidth="300px"
        />

        {}
        {/* {locationOptions && locationOptions.length > 0 && (
          <Select
            options={[{ value: "all", label: "All Locations" }, ...locationOptions]}
            value={bulkSelectedLocation}
            onChange={setBulkSelectedLocation}
            placeholder="Select Location"
            size="small"
          />
        )} */}
      </div>

      <div className={styles.table_container}>
        <Table
          columns={baseColumns}
          data={getCurrentSupplyList()}
          height={300}
          isRowClickable={false}
          loading={suppliesLoading || suppliesFetching}
          showCheckboxes={true}
          onSelectionChange={getCurrentSupplySelectionHandler()}
          selectedIds={getCurrentSelectedSupplies()}
          key={`${suppliesTab}-${(getCurrentSupplyList() || []).length}-${suppliesSearchTerm}`}
        />
      </div>

      <div className={styles.bulk_actions_section}>
        <div className={styles.bulk_actions_group}>
          <Button
            variant="secondary"
            size="small"
            onClick={handleMoveSelectedSupplies}
            disabled={getCurrentSelectedSupplies().length === 0}
          >
            Move Selected ({getCurrentSelectedSupplies().length})
          </Button>

          <Button
            variant="primary"
            size="small"
            onClick={handleMoveAllSupplies}
            disabled={!(getCurrentSupplyList() && getCurrentSupplyList().length > 0)}
          >
            Move All ({getCurrentSupplyList() ? getCurrentSupplyList().length : 0})
          </Button>
        </div>
      </div>

      {}
      {editModal.isOpen && (
        <EditSupplyForm
          supply={editModal.selectedSupply}
          isOpen={editModal.isOpen}
          onClose={() => setEditModal({ isOpen: false, selectedSupply: null })}
          onSubmit={handleUpdateSupply}
        />
      )}

      {}
      {moveModal.isOpen && (
        <MoveSupplyForm
          supplies={moveModal.selectedSupplies}
          isBulkMove={moveModal.isBulkMove}
          isOpen={moveModal.isOpen}
          onClose={() => {
            setMoveModal({ isOpen: false, selectedSupplies: [], isBulkMove: false })
            queryClient.invalidateQueries(suppliesQueryKey)
          }}
          onSubmit={() => {
            setMoveModal({ isOpen: false, selectedSupplies: [], isBulkMove: false })
            queryClient.invalidateQueries(suppliesQueryKey)
          }}
        />
      )}

      {}
      {confirmModal.isOpen && (
        <Modal
          isOpen={confirmModal.isOpen}
          title={confirmModal.title}
          width="500px"
          onClose={() => setConfirmModal({ isOpen: false, supply: null, loading: false, title: "", message: "" })}
          height="auto"
          footer={
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
              <Button
                variant="secondary"
                size="small"
                onClick={() => setConfirmModal({ isOpen: false, supply: null, loading: false, title: "", message: "" })}
                disabled={confirmModal.loading}
              >
                Cancel
              </Button>
              <Button variant="danger" size="small" onClick={confirmDelete} loading={confirmModal.loading}>
                {confirmModal.loading ? "Deleting..." : "Delete"}
              </Button>
            </div>
          }
        >
          <div style={{ padding: "8px 0" }}>
            <p>{confirmModal.message}</p>
          </div>
        </Modal>
      )}
    </div>
  )
}

export default SuppliesSection